/** API route paths (without the global `/api` prefix). Single source of truth. */
export const API_ROUTES = {
    health: '/health',
    rules: '/rules',
    rule: (id) => `/rules/${id}`,
    towers: '/towers',
    tower: (id) => `/towers/${id}`,
    /** Stateless detection: run one rule over one window, return anomalies. */
    detectionRun: '/detection/run',
    /** Drill-down: re-query the site/session behind ONE anomaly, return evidence data. */
    detectionInvestigate: '/detection/investigate',
    projects: '/projects',
    project: (id) => `/projects/${id}`,
    auth: {
        me: '/auth/me',
        logout: '/auth/logout',
        refresh: '/auth/refresh',
        google: '/auth/google',
        googleCallback: '/auth/google/callback',
    },
    users: '/users',
    user: (id) => `/users/${id}`,
    /** Dedicated super_admin management (super_admin-only). */
    superAdmins: '/users/super-admins',
    superAdmin: (id) => `/users/super-admins/${id}`,
    organizations: '/organizations',
    organization: (id) => `/organizations/${id}`,
    /** The caller's own organization (any authenticated member). */
    myOrganization: '/organizations/me',
};
/** Global API prefix applied by the NestJS app. */
export const API_PREFIX = 'api';
/**
 * How far back a detection window may start (`from` on `/detection/run`,
 * relative to now). Enforced server-side (400) and pre-checked in the
 * custom-window UI. One year — the window may reach back this far.
 */
export const MAX_DETECTION_LOOKBACK_MS = 365 * 24 * 3_600 * 1_000;
/**
 * Max span of a single detection window (`to - from`). Independent of the
 * lookback cap: the window may START up to a year back, but a single search
 * may cover at most one month, so the ClickHouse scan stays bounded.
 * Enforced server-side (400) and pre-checked in the custom-window UI.
 */
export const MAX_DETECTION_WINDOW_MS = 31 * 24 * 3_600 * 1_000;
/**
 * Demo metro bounding box (Tel Aviv–Yafo). A default query / detection scope
 * when no project polygon is supplied, so MapLibre renders real geography; swap
 * for your real coverage area.
 */
export const METRO_BBOX = {
    minLng: 34.74,
    minLat: 32.03,
    maxLng: 34.84,
    maxLat: 32.13,
    centerLng: 34.79,
    centerLat: 32.08,
};
/**
 * {@link METRO_BBOX} as a closed GeoJSON polygon (`[lng, lat]`, first === last).
 * Used as the default detection/query scope when no project polygon is supplied
 * (e.g. the stats KPI towers query). Keeps every ClickHouse query bounded.
 */
export const METRO_BBOX_POLYGON = {
    type: 'Polygon',
    coordinates: [
        [
            [METRO_BBOX.minLng, METRO_BBOX.minLat],
            [METRO_BBOX.maxLng, METRO_BBOX.minLat],
            [METRO_BBOX.maxLng, METRO_BBOX.maxLat],
            [METRO_BBOX.minLng, METRO_BBOX.maxLat],
            [METRO_BBOX.minLng, METRO_BBOX.minLat],
        ],
    ],
};
/**
 * Simplified outline of the State of Israel as a closed GeoJSON polygon
 * (`[lng, lat]`, first === last). Used as the default project / detection scope
 * so the demo monitors the whole country rather than a single metro bbox.
 */
export const ISRAEL_POLYGON = {
    type: 'Polygon',
    coordinates: [
        [
            [35.57, 33.28], // Metula — northern tip
            [35.78, 33.0], // Golan Heights (NE)
            [35.57, 32.7], // Sea of Galilee / Jordan valley
            [35.55, 32.4],
            [35.55, 31.76], // north of the Dead Sea
            [35.47, 31.5], // Dead Sea (E border)
            [35.43, 31.1],
            [35.4, 30.9], // Arava valley
            [35.15, 30.5],
            [35.0, 30.0],
            [34.93, 29.55], // Eilat — southern tip
            [34.55, 30.4], // Egypt border (Negev SW)
            [34.27, 31.22], // Rafah / Gaza coast
            [34.55, 31.55],
            [34.63, 31.8],
            [34.75, 32.07], // Tel Aviv–Yafo
            [34.92, 32.5],
            [35.07, 32.8], // Haifa / Akko
            [35.1, 33.05], // Nahariya — Lebanon border (NW)
            [35.3, 33.1],
            [35.57, 33.28], // close ring
        ],
    ],
};
export const KPI_LABELS = {
    towersMonitored: 'TOWERS MONITORED',
    activeAlerts: 'ACTIVE ALERTS',
    rulesEnabled: 'RULES ENABLED',
    meanDetect: 'MEAN DETECT',
    networkHealth: 'NETWORK HEALTH',
};
//# sourceMappingURL=index.js.map